import { useCallback, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { getProductNameByProduct, Product } from "@/types/subscription";
import { useOrganizationStore } from "@/stores/organization";
import { useUserStore } from "@/stores/user";
import { Check, Copy, LoaderCircle } from "lucide-react";
import { ApiKey } from "@/types/api-keys";
import { Input } from "./ui/input";

interface ApiKeysCreateDialogProps {
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export default function ApiKeysCreateDialog({
  controlledOpen,
  onOpenChange,
  trigger,
}: ApiKeysCreateDialogProps) {
  const { user } = useUserStore();
  const { organization, createApiKey } = useOrganizationStore();
  const [apiKey, setApiKey] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [product, setProduct] = useState<Product>("poweron-pipelines");

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(open);
      }
      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  const handleCopy = async () => {
    await navigator.clipboard.writeText(apiKey?.apiKey || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const handleCreateApiKey = async () => {
    if (!organization || !user) return;

    try {
      setIsCreating(true);
      const apiKey = await createApiKey(organization.id, user.id, product);
      setIsCreating(false);
      setApiKey(apiKey);
    } catch (error: any) {
      console.warn(error.message);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent
        className="bg-card"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (apiKey !== null) {
            event.preventDefault();
          }
        }}
      >
        {apiKey === null ? (
          <>
            <DialogHeader className="flex">
              <DialogTitle className="text-gray-1100 dark:text-gray-100">
                Create new secret key
              </DialogTitle>
              <DialogDescription className="text-secondary">
                This API key is for your organization and will be used to
                authenticate requests to Libum APIs without user authentication.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2">
              <Label htmlFor="product">Product</Label>
              <Select
                value={product}
                onValueChange={(val: Product) => setProduct(val)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="poweron-pipelines">
                    PowerOn Pipelines
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="form">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateApiKey}>
                {isCreating && (
                  <LoaderCircle className="flex size-6 animate-spin text-secondary-foreground" />
                )}
                Create secret key
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="flex">
              <DialogTitle className="text-gray-1100 dark:text-gray-100">
                Save your key
              </DialogTitle>
              <DialogDescription className="text-secondary">
                Please save your secret key in a safe place since{" "}
                <span className="font-bold">
                  you will not be able to view it again
                </span>
                . If you do lose it, you&apos;ll need to generate a new one.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 py-2">
              <div className="relative w-full">
                <Input
                  id="sk"
                  className="mb-4 mt-0 pr-[79px] text-gray-1100 dark:text-gray-100"
                  placeholder="sk_...1234"
                  defaultValue={apiKey.apiKey}
                  type="text"
                  readOnly
                />
                <Button
                  size="sm"
                  variant="form"
                  className="absolute right-1.5 top-1.5 gap-1 rounded-xs bg-emerald-400 text-white hover:bg-emerald-500"
                  onClick={handleCopy}
                >
                  {copied ? <Check /> : <Copy />}
                  Copy
                </Button>
              </div>

              <Label>Product</Label>
              <span className="text-sm text-secondary">
                {getProductNameByProduct(apiKey.product as Product)}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="form"
                onClick={() => {
                  setIsOpen(false);
                  setTimeout(() => {
                    setApiKey(null);
                    setIsCreating(false);
                  }, 100);
                }}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
